/**
 * Every word on /about (Asana 1219261192406735), verbatim from the ticket's Final
 * Page Copy with Eric's 2026-10-26 rulings applied (welcome bonus, summon an
 * admin, getting help). The backend's crawler meta page duplicates this file;
 * keep the copy here so the two can be diffed.
 */
import { ELITE_MONTHLY_PRICE_LABEL } from 'constants/elite';

export const ABOUT_TRADER_COUNT_LABEL = '500+';

export const ABOUT_SEO = {
  title: 'About TradeMagic: Peer-to-Peer Magic Card Trading',
  description:
    'TradeMagic is a peer-to-peer trading platform where Magic: The Gathering players trade cards by mail using points instead of cash.',
  canonical: 'https://trademagic.gg/about',
};

export const ABOUT_LINKS = {
  linkedin: 'https://www.linkedin.com/in/ericfreytag/',
  x: 'https://x.com/trademagic_gg',
  bluesky: 'https://bsky.app/profile/trademagic.bsky.social',
  reddit: 'https://reddit.com/r/trademagic',
  site: 'https://trademagic.gg',
};

export const ABOUT_H1 = 'About TradeMagic';

export const ABOUT_INTRO =
  "TradeMagic is a peer-to-peer trading platform where Magic: The Gathering players trade the cards they don't use for the cards they need, using points instead of cash.";

export interface AboutItem {
  heading: string;
  body: string;
}

export const ABOUT_H2 = {
  does: 'What TradeMagic Does',
  different: 'What Makes TradeMagic Different',
  who: 'Who Uses TradeMagic',
  team: 'The Team Behind TradeMagic',
  how: "How Trading Works and How You're Protected",
  facts: 'Key Facts',
  faq: 'Frequently Asked Questions',
} as const;

export const ABOUT_DOES: AboutItem[] = [
  {
    heading: 'Want Lists and Automatic Matching',
    body: "Add the cards you're looking for, and TradeMagic finds traders who have them. When someone wants a card in your collection, it shows up on your Sending tab so you can ship it.",
  },
  {
    heading: 'Trading With Points',
    body: 'Every card has a point value based on its market price, and 100 points equal $1 in card value. You earn points when a card you send arrives and spend them on cards you receive, so you never need a one-for-one match.',
  },
  {
    heading: 'Trade Offers',
    body: "Build a two-way offer with another trader and agree on both sides before anything ships. Each side's cards and point values are visible to both traders the whole time.",
  },
  {
    heading: 'Collection Import',
    body: 'Bring your collection in without retyping it. Imports keep the details that affect value, like finish, condition and language.',
  },
];

export const ABOUT_DIFFERENT: AboutItem[] = [
  {
    heading: 'Points Instead of Cash',
    body: "Points stay inside TradeMagic and can't be cashed out, which keeps the focus on trading cards rather than selling them. New accounts earn up to 1,600 bonus points by completing setup, so you can receive cards before you've sent any.",
  },
  {
    heading: 'An Open Economy',
    body: "The transaction fee rate and the platform's economic health are public. The fee is the only way points leave circulation, and you can see the current rate on this page and in the FAQ.",
  },
  {
    heading: 'Protection on Every Trade',
    body: 'Points are held in escrow from the moment a trade is set up until the card arrives. If something goes wrong, either trader can summon an admin from the trade.',
  },
  {
    heading: 'Built for Every Printing',
    body: 'Trade by exact printing, including foil, etched and non-English cards, with point values that reflect each one. Want lists can accept more than one version of a card.',
  },
  {
    heading: 'Elite Is Cosmetic',
    body: "Elite membership adds badges, avatar borders and profile customization, and Elite members pay no transaction fee. It doesn't change matching, visibility or who you can trade with.",
  },
];

/** "See how TradeMagic {link}." with the link text going to /compare. */
export const ABOUT_COMPARE_LINE = {
  before: 'See how TradeMagic ',
  link: 'compares to other platforms',
  after: '.',
  to: '/compare',
};

export const ABOUT_WHO: string[] = [
  'Players who want to turn cards sitting in a binder into upgrades for the decks they actually play',
  'Commander, Cube, Pauper, Premodern and Modern players looking for specific cards for a build',
  'Collectors chasing exact printings, finishes and languages',
  'Traders in the United States and internationally who prefer trading by mail to selling for cash',
];

export const ABOUT_TEAM: AboutItem[] = [
  {
    heading: 'Eric Freytag, Founder',
    body: 'Eric founded TradeMagic and builds it day to day, across product, engineering and support. He has more than 13 years of experience in product management.',
  },
  {
    heading: 'Our Story',
    body: "TradeMagic launched in November 2025 to make trading Magic cards by mail simple and fair. It's an independent, founder-led company based in the San Francisco Bay Area, and its economy is designed to stay stable as the community grows.",
  },
];

export const ABOUT_LINKEDIN_LINK_TEXT = 'Eric on LinkedIn';

export const ABOUT_HOW: AboutItem[] = [
  {
    heading: 'Getting Started',
    body: "Create an account, verify your phone number and add your shipping address. Then add the cards you want and the cards you're willing to trade.",
  },
  {
    heading: 'Shipping',
    body: 'When a trade opens, the sender ships within 72 hours of the trade opening and marks it shipped. Both traders can follow the trade from their Sending and Receiving tabs.',
  },
  {
    heading: 'Escrow and Payment',
    body: "The receiver's points are held in escrow while the card is on its way. When the receiver confirms the card arrived, the points go to the sender, minus the transaction fee.",
  },
  {
    heading: 'When Something Goes Wrong',
    body: 'If something goes wrong, either trader can summon an admin from the trade. A TradeMagic admin reviews it and resolves it with both traders.',
  },
  {
    heading: 'Getting Help',
    body: 'Email support@trademagic.gg or use the Feedback button.',
  },
];

export interface LiveValues {
  /** "2%" once loaded; undefined while loading or after a failed read. */
  feeLabel?: string;
  /** eliteFeeCopy() output once both inputs are loaded; undefined otherwise. */
  eliteFee?: string;
}

/**
 * The Pricing row. With the live values: "Free to join. 2% transaction fee on
 * completed trades. Optional Elite membership at $5.99/month; no transaction fee."
 * Without them the sentences render without a number, never a fallback figure.
 */
export const pricingRow = ({ feeLabel, eliteFee }: LiveValues): string => {
  const fee = feeLabel
    ? `${feeLabel} transaction fee on completed trades.`
    : 'A transaction fee applies on completed trades.';
  const elite = `Optional Elite membership at ${ELITE_MONTHLY_PRICE_LABEL}/month${eliteFee ? `; ${eliteFee}` : ''}.`;
  return `Free to join. ${fee} ${elite}`;
};

/** FAQ: "Is TradeMagic free?" with the live rate slot. */
export const isFreeAnswer = ({ feeLabel }: LiveValues): string =>
  feeLabel
    ? `Yes. Joining and listing cards is free. A transaction point-fee of ${feeLabel} the total point-value of the trade applies to completed trades, and Elite members pay no fee.`
    : 'Yes. Joining and listing cards is free. A transaction point-fee on the total point-value of the trade applies to completed trades, and Elite members pay no fee.';

export const ABOUT_FAQ_STATIC: AboutItem[] = [
  {
    heading: 'Who runs TradeMagic?',
    body: 'TradeMagic is run by its founder, Eric Freytag, from the San Francisco Bay Area.',
  },
  {
    heading: "What happens if a card doesn't arrive?",
    body: "The receiver's points stay in escrow until the trade is resolved. If something goes wrong, either trader can summon an admin from the trade.",
  },
];

export const ABOUT_FAQ_IS_FREE_HEADING = 'Is TradeMagic free?';

export const ABOUT_FAQ_CLOSING = {
  before: 'Have another question? Read the ',
  link: 'full FAQ',
  after: '.',
  to: '/faq',
};

export type KeyFactValue =
  | { text: string }
  | { text: string; href: string; external?: boolean }
  | { links: { text: string; href: string }[] };

export interface KeyFact {
  label: string;
  value: KeyFactValue;
}

/** Similar platforms: only names published in screens/Public/Compare/compareData.ts, minus the one Decision 4 keeps off this page. */
export const ABOUT_SIMILAR_PLATFORMS = 'TCGplayer Direct, Cardsphere, Deckbox, Facebook Groups';

export const keyFacts = (live: LiveValues): KeyFact[] => [
  { label: 'Company Name', value: { text: 'TradeMagic' } },
  { label: 'Type', value: { text: 'Peer-to-peer Magic: The Gathering card trading platform' } },
  { label: 'Founded', value: { text: 'November 2025' } },
  { label: 'Founder', value: { text: 'Eric Freytag', href: ABOUT_LINKS.linkedin, external: true } },
  { label: 'Headquarters', value: { text: 'San Francisco Bay Area, California' } },
  { label: 'Website', value: { text: ABOUT_LINKS.site, href: ABOUT_LINKS.site, external: true } },
  { label: 'Core Offering', value: { text: 'Card-for-card trading by mail using points instead of cash' } },
  { label: 'Pricing', value: { text: pricingRow(live) } },
  { label: 'Points', value: { text: "100 points equal $1 in card value. Points can't be cashed out." } },
  { label: 'Card Values', value: { text: 'Based on current market prices' } },
  { label: 'Protection', value: { text: 'Points held in escrow until delivery, with admin-reviewed cases' } },
  { label: 'Traders', value: { text: `${ABOUT_TRADER_COUNT_LABEL} active traders` } },
  { label: 'Partners', value: { text: 'TopDecked', href: '/topdecked' } },
  { label: 'Similar Platforms', value: { text: ABOUT_SIMILAR_PLATFORMS } },
  {
    label: 'Social',
    value: {
      links: [
        { text: 'X', href: ABOUT_LINKS.x },
        { text: 'Bluesky', href: ABOUT_LINKS.bluesky },
        { text: 'Reddit', href: ABOUT_LINKS.reddit },
      ],
    },
  },
];
